import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import {
  getAllBankPaymentRequests,
  updateBankPaymentRequestStatus,
  getBankPaymentRequestStats,
  BankPaymentRequest
} from '../../services/bankPaymentService';
import commandService, { Command } from '../../services/commandService';
import { formatPersianDateTime, formatPersianDate } from '../../utils/persianDate';
import socketService from '../../services/socketService';
import API_CONFIG from '../../config/api';
import FormattedNumberInput from '../../components/ui/FormattedNumberInput';

// Transaction interface
interface Transaction {
  _id: string;
  amount: number;
  description: string;
  status: 'pending' | 'completed' | 'failed';
  gateway: string;
  refId?: string;
  paymentDate?: string;
  createdAt: string;
  transactionType: 'request' | 'remaining';
}

// Translate status to Persian
const translateStatus = (status: string): string => {
  const statusMap: Record<string, string> = {
    pending: 'در حال بررسی',
    approved: 'تایید شده',
    rejected: 'تایید نشده',
    no_payment_needed: 'عدم نیاز به پرداخت',
    paid: 'پرداخت شده'
  };
  return statusMap[status] || status;
};

// Get status color
const getStatusColor = (status: string): string => {
  const colorMap: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    no_payment_needed: 'bg-blue-100 text-blue-800',
    paid: 'bg-emerald-100 text-emerald-800'
  };
  return colorMap[status] || 'bg-gray-100 text-gray-800';
};

interface RequestStats {
  totalRequests: number;
  pendingRequests: number;
  approvedRequests: number;
  rejectedRequests: number;
  noPaymentNeededRequests: number;
  requestsToday: number;
}

const BankPaymentRequests: React.FC = () => {
  const [requests, setRequests] = useState<BankPaymentRequest[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<BankPaymentRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<RequestStats>({
    totalRequests: 0,
    pendingRequests: 0,
    approvedRequests: 0,
    rejectedRequests: 0,
    noPaymentNeededRequests: 0,
    requestsToday: 0
  });
  const [selectedRequest, setSelectedRequest] = useState<BankPaymentRequest | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject' | 'no_payment'>('approve');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string>('');
  const [expandedRequestId, setExpandedRequestId] = useState<string | null>(null);
  const [transactions, setTransactions] = useState<Record<string, Transaction[]>>({});
  const [loadingTransactions, setLoadingTransactions] = useState<Record<string, boolean>>({});

  // Rejection problems (reasons for bank payment rejections)
  const [problems, setProblems] = useState<Command[]>([]);
  const [selectedProblems, setSelectedProblems] = useState<string[]>([]);
  const [loadingProblems, setLoadingProblems] = useState(false);

  // Modal form state
  const [modalForm, setModalForm] = useState({
    paymentAmount: '',
    customRejectionReason: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    fetchRequests();
    fetchStats();
  }, []);

  useEffect(() => {
    filterRequests();
  }, [requests, statusFilter, searchTerm]);

  // Setup socket listener for new bank payment requests
  useEffect(() => {
    const handleNewBankPaymentRequest = (notification: any) => {

      // Refresh requests and stats
      fetchRequests();
      fetchStats();

      // Show toast notification
      toast.success('درخواست جدید پرداخت باقیمانده دریافت شد', {
        position: "top-right",
        autoClose: 5000,
      });
    };

    // Set up socket listener
    socketService.setOnNewBankPaymentRequest(handleNewBankPaymentRequest);

    // Cleanup
    return () => {
      socketService.setOnNewBankPaymentRequest(() => { });
    };
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await getAllBankPaymentRequests({ limit: 100 });
      if (response.success) {
        setRequests(response.data as BankPaymentRequest[]);
      }
    } catch (error) {

      toast.error('خطا در دریافت درخواست‌ها');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await getBankPaymentRequestStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {

    }
  };

  const loadProblems = async () => {
    try {
      setLoadingProblems(true);
      const commands = await commandService.getCommands();
      setProblems(commands.problems || []);
    } catch (error) {

      toast.error('خطا در بارگیری دلایل');
    } finally {
      setLoadingProblems(false);
    }
  };

  const filterRequests = () => {
    let filtered = requests;

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(request => request.status === statusFilter);
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(request =>
        request.patientInfo.admissionNumber.toLowerCase().includes(search) ||
        request.patientInfo.firstName.toLowerCase().includes(search) ||
        request.patientInfo.lastName.toLowerCase().includes(search) ||
        request.patientInfo.nationalId.includes(search)
      );
    }

    setFilteredRequests(filtered);
  };

  const handleAction = async (request: BankPaymentRequest, action: 'approve' | 'reject' | 'no_payment') => {
    setSelectedRequest(request);
    setActionType(action);
    setSelectedProblems([]);
    setModalForm({
      paymentAmount: '',
      customRejectionReason: ''
    });

    // Load rejection problems if rejecting
    if (action === 'reject') {
      await loadProblems();
    }

    setShowModal(true);
  };

  const handleModalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (submittingRef.current) return;
    if (!selectedRequest) return;

    if (actionType === 'reject' && selectedProblems.length === 0 && !modalForm.customRejectionReason.trim()) {
      toast.error('لطفا حداقل یک دلیل عدم تایید را انتخاب کنید یا دلیل سفارشی وارد کنید');
      return;
    }

    if (actionType === 'approve' && (!modalForm.paymentAmount || parseFloat(modalForm.paymentAmount) <= 0)) {
      toast.error('مبلغ قابل پرداخت الزامی است و باید بیشتر از صفر باشد');
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);

    try {
      let rejectionReason = '';

      if (actionType === 'reject') {
        // Convert selected problem IDs to text
        const selectedTexts = selectedProblems
          .map(id => problems.find(problem => problem._id === id)?.text)
          .filter(Boolean);

        let problemsText = selectedTexts.length > 0 ? `• ${selectedTexts.join('\n• ')}` : '';

        // Add custom reason if provided
        if (modalForm.customRejectionReason.trim()) {
          if (problemsText) {
            problemsText += `\n• ${modalForm.customRejectionReason.trim()}`;
          } else {
            problemsText = `• ${modalForm.customRejectionReason.trim()}`;
          }
        }

        rejectionReason = problemsText;
      }

      const statusData: any = {
        status: actionType === 'approve' ? 'approved' : actionType === 'reject' ? 'rejected' : 'no_payment_needed'
      };

      if (actionType === 'approve') {
        statusData.paymentAmount = parseFloat(modalForm.paymentAmount);
      } else if (actionType === 'reject') {
        statusData.rejectionReason = rejectionReason;
      }

      const response = await updateBankPaymentRequestStatus(selectedRequest._id, statusData);

      if (response.success) {
        let successMessage = '';
        if (actionType === 'approve') {
          successMessage = 'درخواست تایید شد و مبلغ پرداخت تعیین گردید. پیام اطلاع‌رسانی برای کاربر ارسال شد.';
        } else if (actionType === 'reject') {
          successMessage = 'درخواست رد شد و دلیل عدم تایید برای کاربر ارسال شد.';
        } else {
          successMessage = 'وضعیت درخواست به "عدم نیاز به پرداخت" تغییر یافت و پیام اطلاع‌رسانی برای کاربر ارسال شد.';
        }

        toast.success(successMessage);
        setShowModal(false);
        setSelectedRequest(null);
        setSelectedProblems([]);
        fetchRequests();
        fetchStats();
      }
    } catch (error: any) {

      const errorMessage = error.response?.data?.message || 'خطا در به‌روزرسانی درخواست';
      toast.error(errorMessage);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handlePaymentAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow numbers and decimal point
    if (/^\d*\.?\d*$/.test(value)) {
      setModalForm(prev => ({ ...prev, paymentAmount: value }));
    }
  };

  const handleFormattedPaymentAmountChange = (value: string) => {
    setModalForm(prev => ({ ...prev, paymentAmount: value }));
  };

  const viewReceiptImage = (imagePath: string) => {
    // Create full URL for the file
    const fileUrl = imagePath.startsWith('http')
      ? imagePath
      : `${API_CONFIG.uploadURL}/${imagePath}`;

    // Check if it's a PDF file
    const isPdf = imagePath.toLowerCase().includes('.pdf') || fileUrl.toLowerCase().includes('.pdf');

    if (isPdf) {
      // For PDF files, open in new tab for download/viewing
      window.open(fileUrl, '_blank');
    } else {
      // For image files, show in modal
      setSelectedImageUrl(fileUrl);
      setShowImageModal(true);
    }
  };

  // Fetch transactions for a bank payment request
  const fetchTransactionsForRequest = async (bankPaymentRequestId: string) => {
    try {
      setLoadingTransactions(prev => ({ ...prev, [bankPaymentRequestId]: true }));
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.error('No auth token found');
        return;
      }

      const url = `${API_CONFIG.baseURL}/payments/bank-payment-request/${bankPaymentRequestId}`;
      console.log('Fetching transactions from:', url);

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      console.log('Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('Transaction data received:', data);
        if (data.success) {
          setTransactions(prev => ({ ...prev, [bankPaymentRequestId]: data.data || [] }));
        } else {
          console.error('API returned success: false', data);
        }
      } else {
        const errorData = await response.text();
        console.error('Failed to fetch transactions:', response.status, errorData);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoadingTransactions(prev => ({ ...prev, [bankPaymentRequestId]: false }));
    }
  };

  // Toggle expanded request and fetch transactions
  const toggleExpandRequest = (requestId: string) => {
    if (expandedRequestId === requestId) {
      setExpandedRequestId(null);
    } else {
      setExpandedRequestId(requestId);
      // Fetch transactions if not already fetched
      if (!transactions[requestId]) {
        fetchTransactionsForRequest(requestId);
      }
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">مدیریت درخواست‌های پرداخت باقیمانده</h1>

        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-blue-500 mt-0.5 ml-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div className="text-blue-800 text-sm">
              <p className="font-medium mb-1">راهنمای بررسی درخواست‌ها:</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li><strong>تایید:</strong> درج مبلغ قابل پرداخت + تغییر وضعیت به "تایید شده" + ارسال پیام به کاربر</li>
                <li><strong>عدم تایید:</strong> انتخاب دلیل + تغییر وضعیت به "تایید نشده" + ارسال پیام به کاربر</li>
                <li><strong>عدم نیاز:</strong> تغییر وضعیت به "عدم نیاز به پرداخت" + ارسال پیام به کاربر</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
          <div className="bg-blue-50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-blue-700 mb-1">کل درخواست‌ها</h3>
            <p className="text-2xl font-bold text-blue-900">{stats.totalRequests}</p>
          </div>

          <div className="bg-yellow-50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-yellow-700 mb-1">در حال بررسی</h3>
            <p className="text-2xl font-bold text-yellow-900">{stats.pendingRequests}</p>
          </div>

          <div className="bg-green-50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-green-700 mb-1">تایید شده</h3>
            <p className="text-2xl font-bold text-green-900">{stats.approvedRequests}</p>
          </div>

          <div className="bg-red-50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-red-700 mb-1">تایید نشده</h3>
            <p className="text-2xl font-bold text-red-900">{stats.rejectedRequests}</p>
          </div>

          <div className="bg-indigo-50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-indigo-700 mb-1">عدم نیاز</h3>
            <p className="text-2xl font-bold text-indigo-900">{stats.noPaymentNeededRequests}</p>
          </div>

          <div className="bg-purple-50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-purple-700 mb-1">امروز</h3>
            <p className="text-2xl font-bold text-purple-900">{stats.requestsToday}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1">
            <input
              type="text"
              placeholder="جستجو در شماره پذیرش، نام، کد ملی..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">همه وضعیت‌ها</option>
              <option value="pending">در حال بررسی</option>
              <option value="approved">تایید شده</option>
              <option value="rejected">تایید نشده</option>
              <option value="no_payment_needed">عدم نیاز به پرداخت</option>
            </select>
          </div>

          <button
            onClick={fetchRequests}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
          >
            بارگذاری مجدد
          </button>
        </div>

        {/* Requests Table */}
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : filteredRequests.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-sm">هیچ درخواستی یافت نشد</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">درخواست کننده</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">شماره پذیرش</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">تاریخ و ساعت درخواست</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">تاریخ و ساعت پاسخ</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">جزئیات</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRequests.map((request) => (
                  <React.Fragment key={request._id}>
                    <tr className="hover:bg-gray-50">
                      {/* درخواست کننده */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>
                          <div className="font-medium">
                            {request.patientInfo.firstName} {request.patientInfo.lastName}
                          </div>
                          <div className="text-xs text-gray-500">
                            کد ملی: {request.patientInfo.nationalId}
                          </div>
                        </div>
                      </td>

                      {/* شماره پذیرش */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {request.patientInfo.admissionNumber}
                      </td>

                      {/* تاریخ و ساعت درخواست */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatPersianDateTime(request.createdAt)}
                      </td>

                      {/* تاریخ و ساعت پاسخ */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {request.adminResponse?.responseDate
                          ? formatPersianDateTime(request.adminResponse.responseDate)
                          : '---'
                        }
                      </td>

                      {/* جزئیات */}
                      <td className="px-6 py-4 text-sm">
                        <div className="space-y-2">
                          {/* وضعیت */}
                          <div>
                            <span className={`inline-flex px-2 py-1 text-xs rounded-full font-medium ${getStatusColor(request.status)}`}>
                              {translateStatus(request.status)}
                            </span>
                          </div>

                          {/* اطلاعات اضافی */}
                          <div className="text-xs text-gray-500">
                            <div>تاریخ آزمایش: {formatPersianDate(request.patientInfo.testDate)}</div>
                            {request.description && (
                              <div className="mt-1">توضیحات: {request.description}</div>
                            )}
                          </div>

                          {/* دکمه مشاهده فایل */}
                          <div>
                            <button
                              onClick={() => viewReceiptImage(request.receiptImage)}
                              className="text-blue-600 hover:text-blue-800 text-xs font-medium underline"
                            >
                              مشاهده فایل
                            </button>
                          </div>

                          {/* عملیات */}
                          <div className="flex flex-wrap gap-2 mt-2">
                            {request.status === 'pending' ? (
                              <>
                                <button
                                  onClick={() => handleAction(request, 'approve')}
                                  className="inline-flex items-center px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-md hover:bg-green-200 transition-colors"
                                >
                                  ✓ تایید
                                </button>
                                <button
                                  onClick={() => handleAction(request, 'reject')}
                                  className="inline-flex items-center px-3 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-md hover:bg-red-200 transition-colors"
                                >
                                  ✗ عدم تایید
                                </button>
                                <button
                                  onClick={() => handleAction(request, 'no_payment')}
                                  className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-md hover:bg-blue-200 transition-colors"
                                >
                                  ℹ عدم نیاز
                                </button>
                              </>
                            ) : (
                              <div className="text-xs">
                                {request.adminResponse?.responseDate && (
                                  <div className="text-gray-600 mb-2">
                                    پاسخ داده شده در: {formatPersianDateTime(request.adminResponse.responseDate)}
                                  </div>
                                )}
                                {request.status === 'paid' ? (
                                  <div className="space-y-2">
                                    <div className="text-emerald-700 p-2 bg-emerald-50 rounded font-medium">
                                      ✅ پرداخت با موفقیت انجام شد
                                    </div>
                                    {request.adminResponse?.paymentAmount && (
                                      <div className="text-emerald-600 p-2 bg-emerald-50 rounded">
                                        <strong>💰 مبلغ پرداخت شده:</strong> {request.adminResponse.paymentAmount.toLocaleString('fa-IR')} ریال
                                      </div>
                                    )}
                                    {request.paymentInfo?.paidAt && (
                                      <div className="text-emerald-600 p-2 bg-emerald-50 rounded">
                                        <strong>📅 تاریخ و ساعت پرداخت:</strong> {formatPersianDateTime(request.paymentInfo.paidAt)}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <>
                                    {request.adminResponse?.paymentAmount && (
                                      <div className="text-green-600 p-2 bg-green-50 rounded mb-2">
                                        <strong>مبلغ قابل پرداخت:</strong> {request.adminResponse.paymentAmount.toLocaleString('fa-IR')} ریال
                                      </div>
                                    )}
                                    {request.adminResponse?.rejectionReason && (
                                      <div className="text-red-600 p-2 bg-red-50 rounded text-sm">
                                        <strong>دلیل عدم تایید:</strong> {request.adminResponse.rejectionReason}
                                      </div>
                                    )}
                                    {request.status === 'no_payment_needed' && (
                                      <div className="text-blue-600 font-medium">
                                        ℹ عدم نیاز به پرداخت باقیمانده
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            )}
                          </div>

                          {/* دکمه مشاهده تراکنش‌ها */}
                          {(request.status === 'approved' || request.status === 'paid') && (
                            <button
                              onClick={() => toggleExpandRequest(request._id)}
                              className="mt-2 inline-flex items-center px-3 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-md hover:bg-purple-200 transition-colors"
                            >
                              {expandedRequestId === request._id ? '▼' : '◀'} تراکنش‌ها
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Expanded row for transactions */}
                    {expandedRequestId === request._id && (
                      <tr>
                        <td colSpan={5} className="px-6 py-4 bg-gray-50">
                          <div className="p-4 bg-white rounded border border-gray-200">
                            <h4 className="text-sm font-bold text-gray-700 mb-3">تاریخچه تراکنش‌ها</h4>
                            {loadingTransactions[request._id] ? (
                              <div className="text-center py-4">
                                <div className="inline-block animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-purple-500"></div>
                                <p className="mt-2 text-sm text-gray-600">در حال بارگذاری...</p>
                              </div>
                            ) : transactions[request._id] && transactions[request._id].length > 0 ? (
                              <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                  <thead className="bg-gray-50">
                                    <tr>
                                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">ردیف</th>
                                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">درگاه پرداخت</th>
                                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">مبلغ (ریال)</th>
                                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">تاریخ و ساعت پرداخت</th>
                                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">وضعیت</th>
                                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">کد رهگیری</th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white divide-y divide-gray-200">
                                    {transactions[request._id].map((transaction, index) => (
                                      <tr key={transaction._id}>
                                        <td className="px-3 py-2 text-xs text-gray-900">{index + 1}</td>
                                        <td className="px-3 py-2 text-xs text-gray-900">
                                          {transaction.gateway === 'parsian' ? 'پارسیان' :
                                            transaction.gateway === 'zarinpal' ? 'زرین‌پال' :
                                              transaction.gateway === 'mellat' ? 'ملت' :
                                                transaction.gateway}
                                        </td>
                                        <td className="px-3 py-2 text-xs font-medium text-gray-900">
                                          {new Intl.NumberFormat('fa-IR').format(transaction.amount)}
                                        </td>
                                        <td className="px-3 py-2 text-xs text-gray-900">
                                          {transaction.paymentDate
                                            ? formatPersianDateTime(transaction.paymentDate)
                                            : formatPersianDateTime(transaction.createdAt)
                                          }
                                        </td>
                                        <td className="px-3 py-2">
                                          <span className={`inline-flex px-2 py-0.5 text-xs rounded-full font-medium ${transaction.status === 'completed' ? 'bg-green-100 text-green-800' :
                                            transaction.status === 'failed' ? 'bg-red-100 text-red-800' :
                                              'bg-yellow-100 text-yellow-800'
                                            }`}>
                                            {transaction.status === 'completed' ? 'موفق' :
                                              transaction.status === 'failed' ? 'ناموفق' :
                                                'در انتظار'}
                                          </span>
                                        </td>
                                        <td className="px-3 py-2 text-xs text-gray-900 font-mono">
                                          {transaction.refId || '---'}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500 text-center py-4">هیچ تراکنشی یافت نشد</p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Action Modal */}
      {showModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-bold text-gray-800 mb-4">
              {actionType === 'approve' ? 'تایید درخواست و تعیین مبلغ پرداخت' :
                actionType === 'reject' ? 'عدم تایید درخواست' : 'عدم نیاز به پرداخت'}
            </h3>

            <div className="mb-4 p-3 bg-gray-50 rounded-md">
              <p className="text-sm font-medium">درخواست:</p>
              <p className="text-sm text-gray-600">
                {selectedRequest.patientInfo.firstName} {selectedRequest.patientInfo.lastName} -
                شماره پذیرش: {selectedRequest.patientInfo.admissionNumber}
              </p>
            </div>

            <form onSubmit={handleModalSubmit} className="space-y-4">
              {actionType === 'approve' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    مبلغ قابل پرداخت (ریال) *
                  </label>
                  <FormattedNumberInput
                    value={modalForm.paymentAmount}
                    onChange={handleFormattedPaymentAmountChange}
                    placeholder="مبلغ را وارد کنید"
                    showWords={true}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required={true}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    فقط عدد وارد کنید (مثال: 50000)
                  </p>
                </div>
              )}

              {actionType === 'reject' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      انتخاب دلایل عدم تایید
                    </label>
                    {loadingProblems ? (
                      <div className="text-center py-4">
                        <div className="inline-block animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-red-500"></div>
                        <p className="mt-2 text-sm text-gray-600">در حال بارگیری دلایل...</p>
                      </div>
                    ) : (
                      <Select
                        isMulti
                        options={problems.map(problem => ({
                          value: problem._id,
                          label: problem.text
                        }))}
                        value={problems
                          .filter(problem => selectedProblems.includes(problem._id))
                          .map(problem => ({
                            value: problem._id,
                            label: problem.text
                          }))}
                        onChange={(selectedOptions) => {
                          const selectedIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
                          setSelectedProblems(selectedIds);
                        }}
                        placeholder="دلایل عدم تایید را انتخاب کنید..."
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        controlShouldRenderValue={true}
                        isSearchable={true}
                        noOptionsMessage={() => "هیچ دلیلی یافت نشد"}
                        styles={{
                          control: (provided) => ({
                            ...provided,
                            borderRadius: '0.375rem',
                            borderColor: '#D1D5DB',
                            minHeight: '40px',
                          }),
                          placeholder: (provided) => ({
                            ...provided,
                            color: '#9CA3AF',
                          }),
                          multiValue: (provided) => ({
                            ...provided,
                            backgroundColor: '#FEE2E2',
                            borderRadius: '0.25rem',
                          }),
                          multiValueLabel: (provided) => ({
                            ...provided,
                            color: '#DC2626',
                            fontSize: '0.875rem',
                          }),
                          multiValueRemove: (provided) => ({
                            ...provided,
                            color: '#DC2626',
                            ':hover': {
                              backgroundColor: '#FCA5A5',
                              color: '#991B1B',
                            },
                          }),
                        }}
                      />
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      دلیل سفارشی (اختیاری)
                    </label>
                    <textarea
                      value={modalForm.customRejectionReason}
                      onChange={(e) => setModalForm(prev => ({ ...prev, customRejectionReason: e.target.value }))}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="دلیل سفارشی را وارد کنید..."
                    />
                  </div>

                  {(selectedProblems.length > 0 || modalForm.customRejectionReason.trim()) && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded">
                      <h4 className="text-sm font-medium text-red-800 mb-2">دلایل انتخاب شده:</h4>
                      <div className="text-sm text-red-700">
                        {selectedProblems.map((id) => {
                          const problem = problems.find(r => r._id === id);
                          return problem ? (
                            <div key={id} className="flex items-start mb-1">
                              <span className="ml-2">•</span>
                              <span>{problem.text}</span>
                            </div>
                          ) : null;
                        })}
                        {modalForm.customRejectionReason.trim() && (
                          <div className="flex items-start mb-1">
                            <span className="ml-2">•</span>
                            <span>{modalForm.customRejectionReason.trim()}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {actionType === 'no_payment' && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                  <p className="text-blue-700 text-sm">
                    با انتخاب این گزینه، وضعیت درخواست به "عدم نیاز به پرداخت" تغییر خواهد یافت و پیام اطلاع‌رسانی برای کاربر ارسال خواهد شد.
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                >
                  انصراف
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className={`px-4 py-2 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${actionType === 'approve'
                    ? 'bg-green-500 hover:bg-green-600'
                    : actionType === 'reject'
                      ? 'bg-red-500 hover:bg-red-600'
                      : 'bg-blue-500 hover:bg-blue-600'
                    }`}
                >
                  {submitting
                    ? 'در حال ارسال...'
                    : (actionType === 'approve' ? 'تایید' : actionType === 'reject' ? 'عدم تایید' : 'عدم نیاز')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {showImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 max-w-4xl max-h-[90vh] overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800">تصویر قبض</h3>
              <button
                onClick={() => setShowImageModal(false)}
                className="text-gray-500 hover:text-gray-700"
                aria-label="بستن تصویر"
                title="بستن تصویر"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <img
              src={selectedImageUrl}
              alt="Receipt"
              className="max-w-full max-h-[70vh] object-contain mx-auto"
              onError={(e) => {

                e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2Y5ZmFmYiIvPjx0ZXh0IHg9IjE1MCIgeT0iMTAwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM2YjczODAiIHRleHQtYW5jaG9yPSJtaWRkbGUiPlxuRmlsZSBub3QgZm91bmQ8L3RleHQ+PC9zdmc+';
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default BankPaymentRequests;